<template>
  <div class="images-container">
    <div class="page-header">
      <h2>图片管理</h2>
      <el-button type="primary" @click="showAddDialog">新增图片</el-button>
    </div>

    <!-- 搜索区域 -->
    <div class="search-section">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="车辆ID">
          <el-input v-model="searchForm.vehicleId" placeholder="请输入车辆ID" clearable></el-input>
        </el-form-item>
        <el-form-item label="图片类型">
          <el-select v-model="searchForm.type" placeholder="请选择图片类型" clearable>
            <el-option label="外观" value="exterior"></el-option>
            <el-option label="内饰" value="interior"></el-option>
            <el-option label="发动机" value="engine"></el-option>
            <el-option label="底盘" value="chassis"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 图片列表 -->
    <div class="table-section">
      <el-table :data="imageList" v-loading="loading" border stripe>
        <el-table-column prop="imageId" label="图片ID" width="100"></el-table-column>
        <el-table-column prop="vehicleId" label="车辆ID" width="100"></el-table-column>
        <el-table-column label="图片预览" width="100">
          <template slot-scope="scope">
            <el-image 
              :src="scope.row.url" 
              :preview-src-list="[scope.row.url]"
              style="width: 80px; height: 60px; object-fit: cover;"
              fit="cover">
            </el-image>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="图片类型" width="80">
          <template slot-scope="scope">
            <el-tag :type="getTypeTagType(scope.row.type)">
              {{ getTypeLabel(scope.row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="图片描述" width="150">
          <template slot-scope="scope">
            <el-tooltip :content="scope.row.description" placement="top" :open-delay="500" :enterable="false">
              <span>{{ scope.row.description }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="sortOrder" label="排序" width="60"></el-table-column>
        <el-table-column prop="url" label="图片URL" width="200">
          <template slot-scope="scope">
            <el-tooltip :content="scope.row.url" placement="top" :open-delay="500" :enterable="false">
              <span>{{ scope.row.url }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="showEditDialog(scope.row)">编辑</el-button>
            <el-button size="mini" type="success" @click="showPreviewDialog(scope.row)">预览</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-section">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.current"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>

    <!-- 新增/编辑图片对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px" @close="resetForm">
      <el-form :model="imageForm" :rules="imageRules" ref="imageForm" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="图片ID" prop="imageId">
              <el-input v-model="imageForm.imageId" placeholder="请输入图片ID" :disabled="isEdit"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="车辆ID" prop="vehicleId">
              <el-select v-model="imageForm.vehicleId" placeholder="请选择车辆ID" style="width: 100%">
                <el-option
                  v-for="vehicle in vehicleOptions"
                  :key="vehicle.vehicleId"
                  :label="`${vehicle.vehicleId} - ${vehicle.brand} ${vehicle.model}`"
                  :value="vehicle.vehicleId"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="图片类型" prop="type">
              <el-select v-model="imageForm.type" placeholder="请选择图片类型" style="width: 100%">
                <el-option label="外观" value="exterior"></el-option>
                <el-option label="内饰" value="interior"></el-option>
                <el-option label="发动机" value="engine"></el-option>
                <el-option label="底盘" value="chassis"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="排序" prop="sortOrder">
              <el-input-number v-model="imageForm.sortOrder" :min="0" placeholder="排序" style="width: 100%"></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="图片URL" prop="url">
          <div class="upload-container">
            <el-upload
              class="image-upload"
              action=""
              :http-request="handleImageUpload"
              :show-file-list="false"
              :before-upload="beforeImageUpload"
              accept="image/*"
            >
              <div class="upload-area">
                <div v-if="!imageForm.url" class="upload-placeholder">
                  <i class="el-icon-plus"></i>
                  <div class="upload-text">点击上传图片</div>
                </div>
                <img v-else :src="imageForm.url" class="uploaded-image" />
              </div>
            </el-upload>
            <div v-if="imageForm.url" class="image-actions">
              <el-button size="mini" type="text" @click="previewUploadedImage">预览</el-button>
              <el-button size="mini" type="text" @click="removeUploadedImage">删除</el-button>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="图片描述" prop="description">
          <el-select v-model="imageForm.description" placeholder="请选择图片描述" style="width: 100%" clearable>
            <el-option 
              v-for="option in descriptionOptions" 
              :key="option.value" 
              :label="option.label" 
              :value="option.value">
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>

    <!-- 图片预览对话框 -->
    <el-dialog title="图片预览" :visible.sync="previewDialogVisible" width="800px">
      <div v-if="previewImage" class="image-preview">
        <el-image 
          :src="previewImage.url" 
          style="width: 100%; max-height: 500px; object-fit: contain;"
          fit="contain">
        </el-image>
        <div class="image-info">
          <p><strong>图片ID:</strong> {{ previewImage.imageId }}</p>
          <p><strong>车辆ID:</strong> {{ previewImage.vehicleId }}</p>
          <p><strong>图片类型:</strong> {{ getTypeLabel(previewImage.type) }}</p>
          <p><strong>图片描述:</strong> {{ previewImage.description }}</p>
          <p><strong>排序:</strong> {{ previewImage.sortOrder }}</p>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { imageApi } from '@/api/image'
import { uploadImage } from '@/api/upload'
import { vehicleApi } from '@/api/vehicle'

export default {
  name: 'Images',
  data() {
    return {
      loading: false,
      submitLoading: false,
      imageList: [],
      searchForm: {
        vehicleId: '',
        type: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      previewDialogVisible: false,
      isEdit: false,
      previewImage: null,
      imageForm: {
        imageId: '',
        vehicleId: '',
        url: '',
        type: '',
        description: '',
        sortOrder: 0
      },
      vehicleOptions: [], // 车辆选项列表
      // 图片描述选项
      descriptionOptions: [
        { label: '左前45°', value: '左前45°' },
        { label: '右前45°', value: '右前45°' },
        { label: '右后45°', value: '右后45°' },
        { label: '左后45°', value: '左后45°' },
        { label: '左前门框', value: '左前门框' },
        { label: '左后门框', value: '左后门框' },
        { label: '工作台', value: '工作台' },
        { label: '组合仪表（怠速）', value: '组合仪表（怠速）' },
        { label: '车身大顶', value: '车身大顶' },
        { label: '后行李箱整体', value: '后行李箱整体' },
        { label: '车辆前底盘', value: '车辆前底盘' },
        { label: '车辆后底盘', value: '车辆后底盘' },
        { label: '发动机舱表面', value: '发动机舱表面' }
      ],
      imageRules: {
        imageId: [
          { required: true, message: '请输入图片ID', trigger: 'blur' }
        ],
        vehicleId: [
          { required: true, message: '请选择车辆ID', trigger: 'change' }
        ],
        url: [
          { required: true, message: '请上传图片', trigger: 'change' }
        ],
        type: [
          { required: true, message: '请选择图片类型', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑图片' : '新增图片'
    }
  },
  mounted() {
    this.loadData()
    this.loadVehicleOptions()
  },
  methods: {
    // 加载车辆选项
    async loadVehicleOptions() {
      try {
        const response = await vehicleApi.getVehicleList({ current: 1, size: 1000 })
        if (response && (response.code === 200 || response.code === '200') && response.data) {
          this.vehicleOptions = response.data.records || []
        }
      } catch (error) {
        console.error('加载车辆选项失败:', error)
        this.$message.error('加载车辆选项失败')
      }
    },

    // 加载数据
    async loadData() {
      this.loading = true
      try {
        const params = {
          current: this.pagination.current,
          size: this.pagination.size,
          ...this.searchForm
        }
        const response = await imageApi.getImageList(params)
        if (response && (response.code === 200 || response.code === '200') && response.data) {
          this.imageList = response.data.records || []
          this.pagination.total = response.data.total || 0
        }
      } catch (error) {
        console.error('加载图片数据失败:', error)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        vehicleId: '',
        type: ''
      }
      this.handleSearch()
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.size = size
      this.loadData()
    },

    // 当前页改变
    handleCurrentChange(current) {
      this.pagination.current = current
      this.loadData()
    },

    // 显示新增对话框
    showAddDialog() {
      this.isEdit = false
      this.dialogVisible = true
      // 重置表单数据
      this.imageForm = {
        imageId: '',
        vehicleId: '',
        url: '',
        type: '',
        description: '',
        sortOrder: 0
      }
      // 清空验证状态
      this.$nextTick(() => {
        if (this.$refs.imageForm) {
          this.$refs.imageForm.clearValidate()
        }
      })
    },

    // 显示编辑对话框
    showEditDialog(row) {
      this.isEdit = true
      this.imageForm = { ...row }
      this.dialogVisible = true
    },

    // 预览图片
    showPreviewDialog(row) {
      this.previewImage = row
      this.previewDialogVisible = true
    },

    // 提交表单
    async handleSubmit() {
      this.$refs.imageForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            if (this.isEdit) {
              await imageApi.updateImage(this.imageForm)
              this.$message.success('更新成功')
            } else {
              await imageApi.addImage(this.imageForm)
              this.$message.success('添加成功')
            }
            this.dialogVisible = false
            this.loadData()
          } catch (error) {
            console.error('提交失败:', error)
            this.$message.error('操作失败')
          } finally {
            this.submitLoading = false
          }
        }
      })
    },

    // 删除图片
    handleDelete(row) {
      this.$confirm('确定要删除该图片吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await imageApi.deleteImage(row.imageId)
          this.$message.success('删除成功')
          this.loadData()
        } catch (error) {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },

    // 重置表单
    resetForm() {
      this.$refs.imageForm.resetFields()
      this.imageForm = {
        imageId: '',
        vehicleId: '',
        url: '',
        type: '',
        description: '',
        sortOrder: 0
      }
      // 确保上传区域重置
      this.$nextTick(() => {
        if (this.$refs.imageForm) {
          this.$refs.imageForm.clearValidate()
        }
      })
    },

    // 图片上传处理
    async handleImageUpload(options) {
      try {
        const response = await uploadImage(options.file)
        if (response && (response.code === 200 || response.code === '200')) {
          this.imageForm.url = response.data
          this.$message.success('图片上传成功')
        } else {
          this.$message.error(response?.msg || '图片上传失败')
        }
      } catch (error) {
        console.error('图片上传失败:', error)
        this.$message.error('图片上传失败')
      }
    },

    // 图片上传前校验
    beforeImageUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif';
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isJPG) {
        this.$message.error('图片只能是 JPG/PNG/GIF 格式!');
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 2MB!');
      }
      return isJPG && isLt2M;
    },

    // 预览已上传的图片
    previewUploadedImage() {
      if (this.imageForm.url) {
        this.previewImage = {
          imageId: this.imageForm.imageId,
          vehicleId: this.imageForm.vehicleId,
          type: this.imageForm.type,
          description: this.imageForm.description,
          sortOrder: this.imageForm.sortOrder,
          url: this.imageForm.url
        };
        this.previewDialogVisible = true;
      } else {
        this.$message.warning('请先上传图片');
      }
    },

    // 移除已上传的图片
    removeUploadedImage() {
      this.imageForm.url = '';
      this.$message.success('图片已移除');
    },

    // 获取图片类型标签类型
    getTypeTagType(type) {
      const types = {
        'exterior': 'primary',
        'interior': 'success',
        'engine': 'warning',
        'chassis': 'info'
      }
      return types[type] || 'info'
    },

    // 获取图片类型标签
    getTypeLabel(type) {
      const labels = {
        'exterior': '外观',
        'interior': '内饰',
        'engine': '发动机',
        'chassis': '底盘'
      }
      return labels[type] || type
    }
  }
}
</script>

<style scoped>
.images-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.search-section {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-form {
  margin: 0;
}

.table-section {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.pagination-section {
  padding: 20px;
  text-align: right;
}

/* 操作按钮样式优化 */
.el-button--mini {
  margin-right: 5px;
}

.el-button--mini:last-child {
  margin-right: 0;
}

/* 表格内容垂直居中 */
.el-table .cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.el-table .cell:first-child {
  justify-content: flex-start;
}

/* 操作列按钮容器 */
.el-table .el-button + .el-button {
  margin-left: 5px;
}

/* 图片预览样式 */
.image-preview {
  border-radius: 4px;
  overflow: hidden;
}

.dialog-footer {
  text-align: right;
}

/* 上传区域样式 */
.upload-container {
  position: relative;
  display: inline-block;
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
  background-color: #f5f7fa;
  text-align: center;
  line-height: 100px;
  cursor: pointer;
}

.upload-container:hover {
  border-color: #409eff;
}

.image-upload {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-placeholder {
  color: #909399;
  font-size: 14px;
}

.upload-placeholder i {
  font-size: 28px;
  margin-bottom: 5px;
}

.upload-text {
  margin-top: 5px;
  font-size: 14px;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-actions {
  position: absolute;
  top: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 0 4px 0 4px;
  padding: 2px 5px;
  display: flex;
  gap: 5px;
  z-index: 10;
}

.image-actions .el-button {
  color: #fff;
  font-size: 12px;
}
</style>